import { GetServerSideProps } from 'next';
import cx from 'classnames';
import Link from 'next/link';
import { fetch, resolvePageDataPath } from '@/utils';
import {
  BE_PARTS_NUMBERS_CURSORS_PATH,
  BE_PARTS_NUMBERS_PATH,
} from '@/constant/Paths';
import { PartNumberDataInterface } from '@/types/api';

import HeadSeo from '@/components/_App/HeadSeo';

import styles from './Parts.module.scss';

export default function Parts({
  partsData,
  pagesCursors,
  partsNumbers,
  pageNumber,
}: any) {
  return (
    <>
      <HeadSeo
        title={partsData.metaTitle}
        description={partsData.metaDescription}
      />
      <div className={styles.container}>
        <h1
          className={cx(
            styles.title,
            'text-4xl font-bold text-white leading-[44px]',
          )}
        >
          {partsData.metaTitle}
        </h1>
        <hr className="border-gold-50 my-6" />
        <div>
          <ul className="flex flex-wrap text-white">
            {partsNumbers.map(({ part_number: partNumber }: any) => (
              <li
                className="lg:basis-1/4 md:basis-1/3 basis-2/4"
                key={partNumber}
              >
                <a
                  href={`/part/${partNumber}`}
                  className="inline-block py-1 text-sm"
                >
                  {partNumber}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <ul className="text-white mt-12">
            {pagesCursors.map(({ cursor, page }: any, index: number) => (
              <li className="m-2 inline-block" key={cursor}>
                <Link
                  href={`/parts/${page}`}
                  className={cx(
                    pageNumber === page ? 'bg-red-500' : 'bg-white/50',
                    'inline-block rounded-md p-2 text-sm',
                  )}
                >
                  {index === 0 && pageNumber !== 1
                    ? '<'
                    : pagesCursors.length === index + 1 &&
                      pageNumber !== pagesCursors[pagesCursors.length - 1].page
                    ? '>'
                    : page}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export async function getStaticProps({
  params: { origin, pageNumber = 1 },
}: {
  params: { origin: string; pageNumber: number };
}) {
  const preparedPageNumber = Number(pageNumber);
  if (!Number.isInteger(preparedPageNumber)) {
    return { notFound: true };
  }

  const [partsData, partsNumbersCursorsResponse] = await Promise.all([
    await resolvePageDataPath('parts', origin),
    await fetch(`${BE_PARTS_NUMBERS_CURSORS_PATH}`, undefined, origin),
  ]);

  const cursors = await partsNumbersCursorsResponse.json();

  const currentPageCursor = cursors[preparedPageNumber - 1];

  const partsNumbersResponse = await fetch(
    `${BE_PARTS_NUMBERS_PATH}?page=${currentPageCursor}`,
    undefined,
    origin,
  );

  const partsNumbers: PartNumberDataInterface[] =
    await partsNumbersResponse.json();

  if (partsNumbers.length <= 0) {
    return { notFound: true };
  }

  const preparedCursors = cursors.map((cursor: number, index: number) => ({
    page: index + 1,
    cursor,
  }));

  const currentPageNumberCursorIndex = preparedCursors.findIndex(
    ({ cursor }: any) => cursor === currentPageCursor,
  );

  const pagesCursors =
    preparedCursors.length > 5 && currentPageNumberCursorIndex > 5
      ? preparedCursors.slice(
          currentPageNumberCursorIndex - 6,
          currentPageNumberCursorIndex + 5,
        )
      : preparedCursors.slice(0, currentPageNumberCursorIndex + 5);

  return {
    revalidate: 3600 * 24 * 30, // set revalidate interval of 30 days
    props: {
      pagesCursors,
      partsNumbers,
      partsData,
      pageNumber: preparedPageNumber,
    },
  };
}
